<script lang="ts">
	// Design system components
	import { Alert, IconButton, TemperatureWheel } from '@Shelly/shelly-design-system';
	// Helpers
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import {
		celsius_to_fahrenheit,
		defaultTempFavPositions,
		fahrenheit_to_celsius,
		isWallDisplay,
		real_id,
	} from 'src/helpers/interface_helpers';
	import { unlockPin } from 'src/helpers/pin_lock_helpers';
	import { capFirstChar, wait } from 'src/helpers/utilities';
	// Services
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { devices } from 'src/stores/lists/devices';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';
	// Svelte event dispatcher
	import { beforeUpdate, createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	//Components
	import PinUnlock from 'src/components/PinUnlock.svelte';
	import { getWorkingModeIcon } from 'src/helpers/device_data_helpers';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';

	export let thermostat, device, disabled, isLoading;
	/** Main-tab Wall Display: alerts render in SidepanelContent staticTop, not under the wheel. */
	export let hideInlineAlerts = false;

	const min = thermostat.display_unit === 'F' ? 41 : 5;
	const max = thermostat.display_unit === 'F' ? 95 : 35;
	const unit = thermostat.display_unit;
	let temp_fav_positions =
		(isWallDisplay(device.type)
			? $devices[real_id(device.id)]?.temp_fav_positions
			: device?.temp_fav_positions) || defaultTempFavPositions(unit);

	const dispatch = createEventDispatcher();

	let step = unit === 'C' ? 0.5 : 1;
	let lockValue = false;
	let value = unit === 'F' ? celsius_to_fahrenheit(thermostat.target_C) : thermostat.target_C;
	let cacheValue = value;
	let sendTimeout: NodeJS.Timeout;
	let pinLocked =
		typeof $pin_unlocked_devices[real_id(device.id)] === 'undefined' &&
		$devices[real_id(device.id)]?.pin_lock;
	let showModal = false;

	const temperatureSet = async (_value) => {
		//individual temperature per thermostat
		const data = thermostat;
		data.target_C = unit === 'F' ? fahrenheit_to_celsius(_value) : _value;
		const res = await gen2Request(device, 'Thermostat.SetConfig', {
			id: data.id,
			config: { ...data },
		});

		const response = onSettingsResponse(res, device, {
			success: $format('temp_set_scc_toast', { values: { value: _value } }),
			danger: $format('error_global', { values: { value: _value } }),
		});
		if (response.isok) {
			thermostat = { ...thermostat, ...data };
			dispatch('change', thermostat); //this is used for updating thermostat in drawer
			wait(1000).then(() => {
				//preventing value bounce while new value is set
				lockValue = false;
			});
		}
		return response;
	};

	const onChange = async (e: any) => {
		if (!pinLocked) {
			//debouncing on change for new buttons control
			lockValue = true;
			clearTimeout(sendTimeout);
			sendTimeout = setTimeout(temperatureSet, 1000, e.detail.value);
		} else {
			value = thermostat.target_C;
			showModal = true;
		}
	};

	beforeUpdate(() => {
		if (!lockValue) {
			//getting value from thermostat if not locked
			return (value =
				unit === 'F' ? celsius_to_fahrenheit(thermostat.target_C) : thermostat.target_C);
		}
	});

	const handlePin = () => {
		// using real id because thermostats id is _101 but we need not to change pin lock logic so if device has pin lock it's channel will have it also
		const _device = { ...device };
		_device.id = real_id(device?.id);
		pinLocked = unlockPin(_device, pinLocked);
		onChange({ detail: { value: cacheValue } });
	};

	$: currentTemperatureObject =
		thermostat.current_C <= -273
			? null
			: {
					value: `${thermostat.current_C} °${unit}`,
					description: $format('label_current_temperature', {
						default: 'Current temperature',
					}),
				};

	const a11yProps = {
		tempWheelAriaLabel: $format('label_temperature', {
			default: 'Temperature',
		}),
		thumbAriaLabel: $format('label_temperature_thumb', {
			default: 'Temperature thumb',
		}),
		incrementBtnAriaLabel: $format('button_increase_temperature', {
			default: 'Increase temperature',
		}),
		decrementBtnAriaLabel: $format('button_decrease_temperature', {
			default: 'Decrease temperature',
		}),
	};
</script>

<div class="text-center">
	<TemperatureWheel
		{a11yProps}
		{isLoading}
		{disabled}
		valuePrefix={'°' + unit}
		{value}
		icon={thermostat?.type === 'cooling' && value < thermostat?.current_C
			? 'fas fa-temperature-snow'
			: thermostat?.type === 'heating' && value > thermostat?.current_C
				? 'fas fa-temperature-hot'
				: 'fas fa-thermometer-three-quarters'}
		current={currentTemperatureObject}
		{min}
		{max}
		on:value_change={onChange}
		type={'temp'}
		{step}
		on:move={() => {
			console.warn('move');
		}}
		class="mb-s"
	/>
</div>

<div class="d-flex justify-content-center mb-s">
	{#each temp_fav_positions as pos}
		<IconButton
			title={$format(`temp_fav_position_${pos.mode}`, {
				default: capFirstChar(pos.mode),
			})}
			class="ml-xs mr-xs"
			disabled={!pos?.active || !thermostat?.enable}
			icon={getWorkingModeIcon(pos.mode)}
			variant={`${pos?.active && +value === +pos.value ? 'primary' : 'secondary'}`}
			on:click={() => onChange({ detail: { value: +pos.value } })}
			aria-label={$format(`temp_fav_position_${pos.mode}`, {
				default: capFirstChar(pos.mode),
			})}
		/>
	{/each}
</div>
{#if !hideInlineAlerts && !device?.shared && temp_fav_positions?.filter((pos) => pos.active).length == 0}
	<Alert
		type="info"
		heading={$format('alert_info_heading_temp_fav_positions', {
			default: 'You can enable or disable temperature favorite positions from sensor settings',
		})}
		cta={{
			title: $format('button_enable_disable', {
				default: 'Enable/Disable',
			}),
		}}
		on:cta_click={() => {
			navigateToSetting(real_id(device.id), 'sensor_settings', 'temperature_favorite_positions');
		}}
	/>
{/if}
{#if showModal}
	<PinUnlock bind:showModal on:unlock={handlePin} on:cancel={() => (showModal = false)} />
{/if}
